<template>
  <div class="micro-base-box">
    <div v-for="item in (fusionMode ? [] : microAppList)" :key="item.name" :id="item.name"
      v-show="$route.fullPath.startsWith(item.activeRule)">
    </div>
    <div v-for="item in fusionSlots" :key="item.key" :id="item.key"
      v-show="$route.path.startsWith(item.activeRule) && $route.query.__cmhub_side === item.side"></div>
    <!-- 嵌入式子应用的持久化暂存区：子应用不展示时停放在此处，不可见但不会被销毁 -->
    <div ref="embeddedParkingRef" class="embedded-parking"></div>
  </div>
</template>

<script>
import { loadMicroApp } from 'qiankun';

import mainBus from '@/micro/eventBus';
import { logoutWithRedirect } from '@/utils/logout';
import { getMicroAppList } from '../micro/index';
import { sendAliyunLog } from '@/utils/point';
import { message } from 'ant-design-vue';
import { isCmhub } from '@/features/cmhub/config';
import { fusionContainers, createFusionHost } from '@/features/cmhub/host';
import { SIDE_QUERY } from '@/features/cmhub/navigation';
import { withCmhubProps } from '@/features/cmhub/bridge';
import { loadCmhubResources } from '@/features/cmhub/resources';

// ===== 全局补丁：修复子应用 app 单例重复 use(router) 导致的 $route 重定义问题 =====
// Vue Router 4 在 install 时用 Object.defineProperty 定义 $route（configurable: false）。
// 补丁使 $route/$router 始终为 configurable: true，允许多次定义。
const _origDefineProperty = Object.defineProperty;
Object.defineProperty = function (obj, prop, desc) {
  if (prop === '$route' || prop === '$router') {
    return _origDefineProperty.call(this, obj, prop, { ...desc, configurable: true });
  }
  return _origDefineProperty.call(this, obj, prop, desc);
};

export default {
  name: 'MicroBase',
  data() {
    return {
      microAppList: getMicroAppList(),
      loadedApp: {}, // 已加载的子应用
      loadingApps: {},
      bridgeDestroyed: false,
    };
  },
  computed: {
    fusionMode() { return isCmhub(); },
    fusionSlots() { return this.fusionMode ? fusionContainers(this.microAppList) : []; },
  },
  created() {
    console.warn('micro base created');
    this._embeddedApps = {};
    this._embeddedCounter = 0;
  },
  watch: {
    '$route.fullPath': {
      handler(newVal, oldVal) {
        // ===== 拦截从 ccrm 嵌入子应用发起的跨子应用跳转 =====
        // 当 ccrm 内嵌入的子应用（如 baseFinancial）调用 $mainRouter.push() 跳到非 /ccrm/ 路径时，
        // 路由会跳离 ccrm 上下文。此处通用拦截：检测到上一个路由在 /ccrm/embedded/ 下
        // 且新路由不在 /ccrm/ 下，则自动重定向回 /ccrm/ 前缀下，由 ccrm 内部路由决定如何处理。
        if (
          typeof oldVal === 'string' && oldVal.startsWith('/ccrm/embedded/') &&
          !this.$route.path.startsWith('/ccrm/')
        ) {
          // 将路径重定向为 ccrm 的嵌入式子应用路由，复用 SubAppHost 机制加载并展示
          // 例如 /goodManager/projectDetail → /ccrm/embedded/goodManager/projectDetail
          const redirectPath = '/ccrm/embedded' + this.$route.fullPath;
          console.log('[MAIN-DEBUG] 拦截嵌入子应用跨应用跳转:', this.$route.fullPath, '→', redirectPath);
          this.$router.replace(redirectPath);
          return;
        }

        const hasMicro = this.microAppList
          .find((app) => (this.$route.path.startsWith(app.activeRule)));
        if (hasMicro && hasMicro.name) {
          this.init(hasMicro);
        }
        this.routeCache();
        // 通知 ccrm 子应用同步路由（ccrm 用 memoryHistory，不监听 URL 变化）
        if (this.$route.path.startsWith('/ccrm/') || this.$route.path === '/ccrm') {
          console.log('[MAIN-DEBUG] ccrm-route-sync emit, fullPath:', this.$route.fullPath);
          mainBus.$emit('ccrm-route-sync', this.$route.fullPath);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // ccrm 子应用内部导航时，由主应用 router 更新浏览器 URL
    mainBus.$on("ccrm-url-update", (targetPath) => {
      console.log('[MAIN-DEBUG] ccrm-url-update, targetPath:', targetPath, 'current:', this.$route.fullPath);
      if (this.$route.fullPath !== targetPath) {
        this.$router.push(targetPath);
      }
    });

    // ===== 嵌入式子应用加载 =====
    // 核心设计：
    // 1. 子应用只加载一次，永不卸载（避免 app 单例 dead 后无法恢复）
    // 2. 容器 DOM 由 MicroBase 持有（放在 parking 暂存区），不跟随 SubAppHost 销毁
    // 3. SubAppHost 通过 embedded-show 事件把容器"借"过去显示，embedded-hide 时归还到 parking
    mainBus.$on("embedded-load-micro-app", async (payload) => {
      if (isCmhub()) {
        mainBus.$emit(payload.callbackEvent, { success: false, error: '融合嵌套应用请使用实例加载接口' });
        return;
      }
      const { appKey, config, callbackEvent, hostSlotId } = payload;

      // 已加载过：把容器移到 SubAppHost 指定的挂载点
      if (this._embeddedApps[appKey]) {
        const cached = this._embeddedApps[appKey];
        if (hostSlotId) {
          const slot = document.getElementById(hostSlotId);
          if (slot && cached.containerEl) {
            slot.appendChild(cached.containerEl);
            // 恢复显示（hideApp 会设置 display: none）
            cached.containerEl.style.display = '';
          }
        }
        mainBus.$emit(callbackEvent, { success: true, cached: true });
        return;
      }

      try {
        if (isCmhub()) await loadCmhubResources();
        if (this.bridgeDestroyed) return;
        this._embeddedCounter++;
        const uniqueName = `${config.name}_embedded_${this._embeddedCounter}`;

        // 创建持久化容器（不跟随 SubAppHost 生命周期）
        const containerEl = document.createElement('div');
        containerEl.id = `embedded-container-${appKey}-${this._embeddedCounter}`;
        containerEl.style.height = '100%';

        // 如果 SubAppHost 提供了挂载点，直接放进去；否则放到 parking 暂存区
        if (hostSlotId) {
          const slot = document.getElementById(hostSlotId);
          if (slot) {
            slot.appendChild(containerEl);
          } else {
            this.$refs.embeddedParkingRef.appendChild(containerEl);
          }
        } else {
          this.$refs.embeddedParkingRef.appendChild(containerEl);
        }

        const loadConfig = {
          ...config,
          name: uniqueName,
          container: containerEl, // 直接传 DOM 元素，不依赖选择器
        };

        console.log('[MAIN-DEBUG] embedded-load-micro-app, appKey:', appKey, 'uniqueName:', uniqueName);
        // 使用默认 proxy sandbox，确保子应用之间脚本隔离
        const parcel = this.loadWithContext(loadConfig);

        this._embeddedApps[appKey] = { parcel, containerEl, uniqueName };

        parcel.mountPromise
          .then(() => {
            console.log('[MAIN-DEBUG] embedded mount success, appKey:', appKey);
            mainBus.$emit(callbackEvent, { success: true });
          })
          .catch((err) => {
            console.error('[MAIN-DEBUG] embedded mount failed:', err);
            delete this._embeddedApps[appKey];
            if (containerEl.parentNode) containerEl.parentNode.removeChild(containerEl);
            mainBus.$emit(callbackEvent, { success: false, error: String(err) });
          });
      } catch (err) {
        console.error('[MAIN-DEBUG] embedded loadMicroApp failed:', err);
        mainBus.$emit(callbackEvent, { success: false, error: String(err) });
      }
    });

    // SubAppHost 显示某个子应用：把容器从 parking 移到 SubAppHost 的挂载点
    mainBus.$on("embedded-show", (payload) => {
      const { appKey, hostSlotId } = payload;
      const cached = this._embeddedApps[appKey];
      if (!cached || !cached.containerEl) return;
      const slot = document.getElementById(hostSlotId);
      if (slot) {
        slot.appendChild(cached.containerEl);
        cached.containerEl.style.display = 'block';
      }
    });

    // SubAppHost 隐藏某个子应用：把容器移回 parking 暂存区
    mainBus.$on("embedded-hide", (payload) => {
      const { appKey } = payload;
      const cached = this._embeddedApps[appKey];
      if (!cached || !cached.containerEl) return;
      cached.containerEl.style.display = 'none';
      if (this.$refs.embeddedParkingRef) {
        this.$refs.embeddedParkingRef.appendChild(cached.containerEl);
      }
    });

    // SubAppHost 销毁时：把所有相关容器移回 parking（不卸载子应用）
    mainBus.$on("embedded-park-all", (payload) => {
      const { appKeys } = payload;
      if (!appKeys || !appKeys.length) return;
      appKeys.forEach((appKey) => {
        const cached = this._embeddedApps[appKey];
        if (cached && cached.containerEl && this.$refs.embeddedParkingRef) {
          cached.containerEl.style.display = 'none';
          this.$refs.embeddedParkingRef.appendChild(cached.containerEl);
        }
      });
    });

    // 低代码传来的跨应用关闭页签
    mainBus.$on("lowcode_closeCurTab", (push) => {
      this.$store.commit('delCurTab', this.$route.fullPath);
      push();
    });
    // 低代码传来的跨应用跳转
    mainBus.$on("lowcode_mainRouter", (info) => {
      this.$router.push(info);
    });
    // zms传来的跨应用关闭页签
    mainBus.$on("zms_closeCurTab", () => {
      this.$store.commit('delCurTab', this.$route.fullPath);
    });
    // 监听登出事件
    mainBus.$on("logout", async (options = {}) => {
      const redirectToRoot = options?.redirectToRoot === true;
      await logoutWithRedirect({
        router: redirectToRoot ? null : this.$router,
        routePath: this.$route.path,
        reloadOnFallback: !redirectToRoot,
        fallbackToLogin: !redirectToRoot,
      });
      if (redirectToRoot) {
        window.location.replace('/');
      }
    });
  },
  methods: {
    onCmhubInvalidated(side) {
      message.warning(`${side === 'customer' ? '客' : '商'}系统登录已失效，请重新登录`);
      mainBus.$emit('cmhub-auth-invalidated', side);
    },
    loadWithContext(config, ...options) {
      return loadMicroApp(withCmhubProps(config, this.microAppList, this.onCmhubInvalidated), ...options);
    },
    // apaas 返回上一级 路由存储
    routeCache() {
      let cacheData = localStorage.getItem('routeCache');
      if (!cacheData) {
        cacheData = [];
      } else {
        cacheData = JSON.parse(cacheData);
      }
      if (this.$route.path && this.$route.query) {
        cacheData.push({
          path: this.$route.path,
          query: this.$route.query,
        });
      }
      localStorage.setItem('routeCache', JSON.stringify(cacheData));
    },
    async init(microApp) {
      if (isCmhub()) {
        try {
          const side = this.$route.query[SIDE_QUERY];
          await loadCmhubResources();
          if (this.bridgeDestroyed) return;
          if (!this._fusionHost) this._fusionHost = createFusionHost({
            apps: this.microAppList, router: this.$router, load: loadMicroApp, bus: mainBus,
            onInvalidated: this.onCmhubInvalidated, closeTab: this.closeCurTab,
            utilities: { sendAliyunLog },
            onError: error => message.error(error.message || '融合应用加载失败'),
          });
          await this._fusionHost.ensure(microApp, side, () => this.$nextTick());
        } catch (error) { message.error(error.message || '融合应用加载失败'); }
        return;
      }
      console.warn(`[CCRM-BUG] MicroBase.init, app=${microApp.name}, alreadyLoaded=${!!this.loadedApp[microApp.name]}, loadedApps=${Object.keys(this.loadedApp).join(',')}, route=${this.$route.fullPath}`);
      if (!this.loadedApp[microApp.name] && !this.loadingApps[microApp.name]) {
        this.loadingApps[microApp.name] = true;
        try {
          if (isCmhub()) await loadCmhubResources();
          if (this.bridgeDestroyed) return;
          // 注入 父元素方法
          /* eslint-disable */
          microApp.props.util = {
            closeCurTab: this.closeCurTab,
            mainRouter: this.$router,
            loadMicroApp: this.loadWithContext,
            resolveEmbeddedMicroApp: this.resolveEmbeddedMicroApp,
            sendAliyunLog: sendAliyunLog,
          };
          /* eslint-enable */
          console.warn(`[CCRM-BUG] loadMicroApp called for ${microApp.name}`);
          const app = this.loadWithContext(microApp);
          this.loadedApp[microApp.name] = { app };
          await app.mountPromise;
        } catch (e) {
          delete this.loadedApp[microApp.name];
          if (isCmhub()) message.error(e.message || '微应用加载失败');
          console.log(e);
        } finally {
          delete this.loadingApps[microApp.name];
        }
      }
    },
    closeCurTab(cb) {
      this.$store.commit('delCurTab', this.$route.fullPath);
      if (cb && typeof cb === 'function') {
        cb();
      } else {
        const panes = this.$store.state.tabs.list;
        if (panes.length > 0) {
          this.$router.push(panes[panes.length - 1].path);
        }
      }
    },
    resolveEmbeddedMicroApp(appKey) {
      const normalizedKey = (appKey || '').replace(/^\/+|\/+$/g, '');
      const matchedApp = this.microAppList.find((item) => {
        const itemKey = item.activeRule.replace(/^\/+|\/+$/g, '');
        return itemKey === normalizedKey || item.name === normalizedKey;
      });

      if (!matchedApp) {
        return null;
      }

      return withCmhubProps({
        name: matchedApp.name,
        entry: matchedApp.entry,
        activeRule: matchedApp.activeRule,
        project: matchedApp.project,
        props: {
          _app: {
            name: matchedApp.name,
            activeRule: matchedApp.activeRule,
            project: matchedApp.project,
          },
          _setGlobalData: mainBus,
          _zntry: matchedApp.props?._zntry,
        },
      }, this.microAppList, this.onCmhubInvalidated);
    },
  },
  activated() {
    console.log('micro base activated');
  },
  deactivated() {
    console.log('micro base deactivated');
  },
  beforeDestroy() {
    this.bridgeDestroyed = true;
    if (this._fusionHost) this._fusionHost.dispose();
    // 销毁时卸载所有已加载的子应用，防止僵尸实例继续响应路由变化
    Object.keys(this.loadedApp).forEach((name) => {
      const { app } = this.loadedApp[name] || {};
      if (app && typeof app.unmount === 'function') {
        console.warn(`[CCRM-BUG] MicroBase.beforeDestroy: unmounting ${name}`);
        try {
          app.unmount();
        } catch (e) {
          console.error(`[MicroBase] unmount ${name} failed:`, e);
        }
      }
    });
    this.loadedApp = {};
  },
};
</script>

<style lang="less" scoped>
.micro-base-box {
  min-width: 1100px;
  height: 100%;
}
/* 暂存区：不可见但保持子应用 DOM 存活 */
.embedded-parking {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
}
</style>
